import { useEffect } from 'react';

interface SEOProps {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
}

const defaultMeta = {
    title: 'Palm Heist - Pirate-Themed Mystery Card Game',
    description: 'Palm Heist is the ultimate blockchain-powered mystery card game on Solana Network. Win big with every card flip in our provably fair gaming platform!',
    image: 'https://palm-heist-auction-keoxfkc0k-palmheist-devs.vercel.app/og-image.jpg', // Use absolute URL
    url: 'https://www.palmheist.com',
};

export default function SEO({ 
    title = defaultMeta.title,
    description = defaultMeta.description,
    image = defaultMeta.image,
    url = defaultMeta.url 
}: SEOProps) {
    const fullTitle = title === defaultMeta.title ? title : `${title} | Palm Heist`;

    useEffect(() => {
        // Update title
        document.title = fullTitle;

        // Update meta tags
        const metaTags = {
            'title': fullTitle,
            'description': description,
            'og:type': 'website',
            'og:url': url,
            'og:title': fullTitle,
            'og:description': description,
            'og:image': image,
            'og:image:width': '1200',
            'og:image:height': '630',
            'og:site_name': 'Palm Heist',
            'twitter:card': 'summary_large_image',
            'twitter:url': url,
            'twitter:title': fullTitle,
            'twitter:description': description,
            'twitter:image': image,
            'twitter:creator': '@PalmHeist',
            'keywords': 'Palm Heist, Mystery Card Game, Solana, NFT, Crypto, Blockchain, Pirate Game, Card Flip, Provably Fair Gaming',
            'robots': 'index, follow',
            'language': 'English',
            'author': 'Palm Heist Team',
            'viewport': 'width=device-width, initial-scale=1.0',
            'revisit-after': '7 days'
        };

        // Update or create meta tags
        Object.entries(metaTags).forEach(([name, content]) => {
            // Handle Open Graph and Twitter tags differently
            if (name.startsWith('og:')) {
                updateOrCreateMetaTag('property', name, content);
            } else if (name.startsWith('twitter:')) {
                updateOrCreateMetaTag('name', name, content);
            } else {
                updateOrCreateMetaTag('name', name, content);
            }
        });

        // Update favicon links
        const favicons = [
            { rel: 'icon', href: '/favicon.ico' },
            { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
            { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
            { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
            { rel: 'manifest', href: '/site.webmanifest' }
        ];

        favicons.forEach(({ rel, href, sizes, type }) => {
            let link = document.querySelector(`link[rel="${rel}"][href="${href}"]`) as HTMLLinkElement;
            if (!link) {
                link = document.createElement('link') as HTMLLinkElement;
                link.rel = rel;
                link.href = href;
                if (sizes) link.setAttribute('sizes', sizes);
                if (type) link.setAttribute('type', type);
                document.head.appendChild(link);
            }
        });

        // Cleanup function
        return () => {
            // Optional: Remove meta tags on component unmount if needed
        };
    }, [fullTitle, description, image, url]);

    return null; // This component doesn't render anything
}

// Helper function to update or create meta tags
function updateOrCreateMetaTag(attributeName: string, name: string, content: string) {
    // Try to find existing tag
    let element = document.querySelector(`meta[${attributeName}="${name}"]`) as HTMLMetaElement;
    
    // If it doesn't exist, create it
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, name);
        document.head.appendChild(element);
    }
    
    // Set the content
    element.setAttribute('content', content);
}
